import { useState } from 'react';
import {
  IconButton,
  Stack,
  styled,
  TableCell,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { DialogEditProducts } from '../DialogEditProducts';
import {
  FormDropdownMenuController,
  FormInputNumberController,
  FormLanguageInputController,
  FormPriceDifferenceInputController,
  SortableTableContents,
} from '@/components';
import {
  HandleMenuQuizAnswerData,
  Ingredient,
  Language,
  onQueryError,
  onQuerySuccess,
  QuizAnswer,
  TaxGroup,
  useMenu,
} from '@/core';
import { LanguageTypes } from '@/features';
import palette from '@/theme/palette.ts';

import { styles } from './styles';

type QuizAnswerFormData = {
  name: Language;
  imageId?: number;
  totalPrice: string;
  taxGroupId: number;
  maxAmount: string;
  kitchenNote: string;
  productIngredients: Ingredient[];
  taxGroup: TaxGroup;
  ordering: number;
};

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: palette.surface.darkest,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: palette.surface.darkest,
    borderRadius: '10px',
    padding: '8px 16px',
  },
}));

const convertQuizAnswerToApiFormat = (answer: QuizAnswerFormData): HandleMenuQuizAnswerData => ({
  name: answer.name,
  kitchen_note: answer.kitchenNote,
  total_price: answer.totalPrice || null,
  tax_in_percent: answer.taxGroup.value,
  image_id: answer.imageId ?? null,
  ordering: answer.ordering,
  max_amount: answer.maxAmount ? Number(answer.maxAmount) : null,
});

const convertTotalPriceToReadableFormat = (totalPrice?: string) => {
  const numberTotalPrice = Number(totalPrice);
  if (isNaN(numberTotalPrice) || numberTotalPrice === 0) return '';
  if (numberTotalPrice > 0) return `+${numberTotalPrice}`;
  return `${numberTotalPrice}`;
};

export const VariantQuizAnswer = ({ answer }: { answer: QuizAnswer }) => {
  const { t } = useTranslation();
  const [ingredientsDialogOpen, setIngredientsDialogOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageTypes>('cs');
  const {
    mutations: { updateMenuQuizAnswerMutation, deleteMenuQuizAnswerMutation },
  } = useMenu();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();

  const methods = useForm<QuizAnswerFormData>({
    defaultValues: {
      name: answer.name,
      imageId: answer.image_id,
      totalPrice: convertTotalPriceToReadableFormat(answer.total_price),
      taxGroupId: 0,
      maxAmount: answer.max_amount?.toString() || '',
      kitchenNote: answer.kitchen_note,
      productIngredients: [],
      ordering: answer.ordering,
    },
    mode: 'onChange',
  });

  const onSuccess = () => {
    onQuerySuccess({
      queryClient,
      queryKeysToInvalidate: [['menuQuizzes', Number(id)]],
    });
  };

  const onDeleteError = () => {
    onQueryError({ errorMessage: t('VARIANT.ANSWER_DELETE_FAILURE') });
  };

  const onUpdateError = () => {
    onQueryError({ errorMessage: t('VARIANT.ANSWER_EDIT_FAILURE') });
  };

  const handleDeleteQuizAnswerClick = async () => {
    const deleteResponse = await deleteMenuQuizAnswerMutation.mutateAsync(answer.id);
    if (deleteResponse.status === 200 && deleteResponse.data) {
      onSuccess();
    } else {
      onDeleteError();
    }
  };

  const handleQuizAnswerChange = async () => {
    const formValues = methods.getValues();
    const convertedAnswer = convertQuizAnswerToApiFormat(formValues);
    const updateResponse = await updateMenuQuizAnswerMutation.mutateAsync({
      answerId: answer.id,
      data: convertedAnswer,
    });
    if (updateResponse.status === 200 && updateResponse.data) {
      onSuccess();
    } else {
      onUpdateError();
    }
  };

  return (
    <SortableTableContents.Item id={answer.id}>
      <FormProvider {...methods}>
        <TableCell align="left">
          <SortableTableContents.DragHandle />
        </TableCell>
        <TableCell align="left">
          <FormLanguageInputController
            label={''}
            controllerName={'name'}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            fieldOnBlur={handleQuizAnswerChange}
          />
        </TableCell>
        <TableCell align="left">
          <FormInputNumberController
            label={''}
            controllerName={'maxAmount'}
            fieldOnBlur={handleQuizAnswerChange}
            isWholeNumber
          />
        </TableCell>
        <TableCell align="left">
          <Stack direction="row" gap={1} alignItems="center">
            <FormPriceDifferenceInputController
              label={''}
              controllerName={'totalPrice'}
              fieldOnBlur={handleQuizAnswerChange}
            />
            <Typography variant="body2" color="text.secondary">
              {t('GLOBAL.CZ_CURRENCY')}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell align="left">
          <FormDropdownMenuController
            controllerName={'taxGroupId'}
            sx={{ width: '130px' }}
            preSelectedTaxValue={answer.tax_value}
            dropdownOnChange={handleQuizAnswerChange}
          />
        </TableCell>
        <TableCell align="right" sx={styles.shrinkCell}>
          <DialogEditProducts
            answer={answer}
            open={ingredientsDialogOpen}
            setOpen={setIngredientsDialogOpen}
          />
          <BootstrapTooltip title={t('VARIANT.PRODUCTS_TOOLTIP')} placement="top">
            <IconButton size="medium" onClick={() => setIngredientsDialogOpen(true)}>
              <span className="material-symbols-rounded" style={{ color: palette.primary.main }}>
                grocery
              </span>
            </IconButton>
          </BootstrapTooltip>
        </TableCell>
        <TableCell align="right" sx={styles.shrinkCell}>
          <IconButton size="medium" onClick={handleDeleteQuizAnswerClick}>
            <span className="material-symbols-rounded">close</span>
          </IconButton>
        </TableCell>
      </FormProvider>
    </SortableTableContents.Item>
  );
};
